import React from 'react';
import {
    Box,
    Checkbox,
    Chip,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    noCommentStyles,
    statusChipStyles,
    tableCellStyles
} from '../../../ExitRequestResponseUnified/ExitRequestResponseStyles';
import {
    COLUMN_CONFIG,
    UI_CONSTANTS,
    LABELS,
    DEFAULTS
} from './AffectedProductsTab.constants';
import { getStatusConfig, isProductDisabled } from './AffectedProductsTab.utils';

const StatusIcon = ({ iconType }) => {
    const icons = {
        DoDisturbIcon: <DoDisturbIcon />,
        CheckCircleIcon: <CheckCircleIcon />,
        HelpOutlineIcon: <HelpOutlineIcon />
    };
    return icons[iconType] || null;
};

export const ProductTableRow = ({
                                    product,
                                    isSelected,
                                    isPlanned,
                                    readOnly,
                                    onSelectProduct,
                                    onOpenComment,
                                    theme,
                                    isEven
                                }) => {
    const statusConfig = getStatusConfig(product.isAffected);

    return (
        <Box sx={styles.rowContainer(theme, isEven)}>
            {/* Checkbox Column */}
            <Box sx={styles.checkboxCell(theme, COLUMN_CONFIG.select.flex)}>
                {!readOnly && (
                    <Checkbox
                        size="small"
                        color="primary"
                        disabled={isProductDisabled(readOnly, isPlanned)}
                        checked={isSelected}
                        onChange={() => onSelectProduct(product.productId)}
                    />
                )}
            </Box>

            {/* Product Name Column */}
            <Box sx={styles.textCell(theme, COLUMN_CONFIG.productName.flex)}>
                {product.productName}
            </Box>

            {/* CUC Number Column */}
            <Box sx={styles.textCell(theme, COLUMN_CONFIG.productCucNumber.flex)}>
                {product.productCucNumber || DEFAULTS.EMPTY_CUC}
            </Box>

            {/* Brand Column */}
            <Box sx={styles.textCell(theme, COLUMN_CONFIG.brand.flex)}>
                {product.brand || (
                    <Typography component="span" sx={styles.naText(theme)}>—</Typography>
                )}
            </Box>

            {/* Countries Column */}
            <Box sx={{ ...styles.textCell(theme, COLUMN_CONFIG.countries.flex), display: 'flex', flexWrap: 'wrap', gap: 0.3, alignItems: 'center' }}>
                {(product.countries || []).length > 0 ? (
                    product.countries.map((c) => (
                        <Chip key={c.id || c} label={c.name || c} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    ))
                ) : (
                    <Typography component="span" sx={styles.naText(theme)}>—</Typography>
                )}
            </Box>

            {/* Status Column */}
            <Box sx={styles.statusCell(theme, COLUMN_CONFIG.affected.flex)}>
                <Chip
                    size="small"
                    label={statusConfig.label}
                    color={statusConfig.color}
                    icon={<StatusIcon iconType={statusConfig.icon} />}
                    sx={statusChipStyles(statusConfig.color)}
                />
            </Box>

            {/* Comments Column */}
            <Box sx={styles.commentCell(theme, COLUMN_CONFIG.comments.flex)}>
                {product.comment ? (
                    <Tooltip title={LABELS.PREVIEW_COMMENT} arrow placement="top">
                        <IconButton
                            size="small"
                            onClick={() => onOpenComment(product.comment)}
                            sx={styles.iconButton(theme)}
                        >
                            <InfoOutlinedIcon sx={styles.infoIcon(theme)} />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Typography component="span" sx={styles.naText(theme)}>
                        {LABELS.NA}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const styles = {
    rowContainer: (theme, isEven) => ({
        display: 'flex',
        width: '100%',
        ...(isEven && { bgcolor: theme.palette.grey[50] })
    }),
    checkboxCell: (theme, flex) => ({
        ...tableCellStyles(theme),
        flex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0px'
    }),
// ... continuing from previous
    textCell: (theme, flex) => ({
        ...tableCellStyles(theme),
        flex,
        fontSize: UI_CONSTANTS.FONT_SIZES.CELL
    }),
    statusCell: (theme, flex) => ({
        ...tableCellStyles(theme),
        flex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }),
    commentCell: (theme, flex) => ({
        ...tableCellStyles(theme),
        flex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }),
    iconButton: (theme) => ({
        p: UI_CONSTANTS.ICON_BUTTON.PADDING,
        minWidth: UI_CONSTANTS.ICON_BUTTON.MIN_WIDTH,
        height: UI_CONSTANTS.ICON_BUTTON.HEIGHT,
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    }),
    infoIcon: (theme) => ({
        fontSize: UI_CONSTANTS.ICON_SIZE,
        color: theme.palette.primary.main
    }),
    naText: (theme) => ({
        ...noCommentStyles(theme),
        fontSize: UI_CONSTANTS.FONT_SIZES.NA
    })
};